import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import moment from "moment-timezone";
import { Field, Formik } from "formik";
import styled from "styled-components";

// components
import { Layout, Loader } from "src/components";
import AssignmentCalendar from "./partials/AssignmentCalendar/AssignmentCalendar";
import { ViewToggleWrapper, ViewToggleButton } from "./partials/AssignmentCalendar/AssignmentCalendar.styles";

// assets
import arrowDownIcon from "src/assets/images/down-arrow.svg";

// hooks
import useSkin from "src/hooks/useSkin";

// utils
import { getAssignmentsActions } from "src/redux/actions/assignments";
import { RootReducer } from "src/redux/reducers";

const ProgressWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressBar = styled.div<{ $percent: number }>`
  flex: 1;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
  min-width: 50px;

  &::after {
    content: "";
    display: block;
    height: 100%;
    width: ${({ $percent }) => $percent}%;
    background: ${({ $percent }) => ($percent >= 75 ? "#28a745" : $percent >= 40 ? "#ffc107" : "#dc3545")};
    border-radius: 3px;
    transition: width 0.3s ease;
  }
`;

const ProgressLabel = styled.span`
  font-size: 13px;
  white-space: nowrap;
  color: #495057;
`;

interface Assignment {
  id: number;
  startDate: Date;
  dueDate: Date;
  name: string;
  group: string;
  isPublished: boolean;
  usersCompleted: number;
  usersAssigned: number;
}

const columnHelper = createColumnHelper<Assignment>();

const assignmentsTypeConfig = [
  { id: "active", label: "Active", value: "active", stateId: 2 },
  { id: "pending", label: "Pending", value: "pending", stateId: 3 },
  { id: "completed", label: "Completed", value: "completed", stateId: 4 },
  { id: "draft", label: "Draft", value: "draft", stateId: 1 }
] as const;

type ViewMode = "list" | "calendar";

const Assignments = () => {
  const [t] = useTranslation();
  const { isGriffle } = useSkin();
  const assignmentsRef = useRef<HTMLDivElement>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const location = useLocation();
  const showOverdue = (location.state as { filter?: string } | null)?.filter === "overdue";
  const [assignmentsType, setAssignmentsType] = useState<(typeof assignmentsTypeConfig)[number]["value"]>(
    assignmentsTypeConfig[0].value
  );
  const dispatch = useDispatch();

  const {
    assignments: { assignments, isLoading }
  }: RootReducer = useSelector<RootReducer>((state) => state) as RootReducer;

  const getAssignments = useCallback(
    () =>
      dispatch(
        getAssignmentsActions.request({
          state: assignmentsFilterRef?.current?.stateId ?? assignmentsTypeConfig[0].stateId
        })
      ),
    [dispatch]
  );

  useEffect(() => {
    setTimeout(() => {
      getAssignments();
    }, 500);
  }, []);

  const data = useMemo(() => {
    if (assignments) {
      const mapped = assignments.map(
        ({ assignmentName, dueDate, startDate, assignmentId, groups, isPublished, usersCompleted, usersAssigned }) => {
          const groupLabel = (value: unknown): string | undefined => {
            if (typeof value === "string") return value.length > 0 ? value : undefined;
            if (value && typeof value === "object") {
              const g = value as { name?: unknown; group?: unknown };
              const label = g.name ?? g.group;
              return typeof label === "string" && label.length > 0 ? label : undefined;
            }
            return undefined;
          };
          const filteredGroups: string[] = (groups || [])
            .map(groupLabel)
            .filter((value): value is string => typeof value === "string");

          return {
            id: assignmentId,
            startDate,
            dueDate,
            name: assignmentName || "-",
            group: filteredGroups.length == 1 ? filteredGroups[0] : filteredGroups?.join(", "),
            isPublished: isPublished,
            usersCompleted: usersCompleted ?? 0,
            usersAssigned: usersAssigned ?? 0
          };
        }
      );
      if (showOverdue) {
        const now = moment();
        return mapped.filter((a) => moment(a.dueDate).isBefore(now));
      }
      return mapped;
    } else {
      return [];
    }
  }, [assignments, showOverdue]);

  const assignmentsFilter = useMemo(
    () => assignmentsTypeConfig.find(({ value }) => value === assignmentsType),
    [assignmentsType, assignmentsTypeConfig]
  );

  const assignmentsFilterRef = useRef(assignmentsFilter);

  useEffect(() => {
    assignmentsFilterRef.current = assignmentsFilter;
    getAssignments();
  }, [assignmentsFilter]);

  const columns = [
    columnHelper.accessor("startDate", {
      cell: (info) => moment(info.getValue()).format("YYYY-MM-DD"),
      header: () => <span>{t("assignments_modal_calendar_label_start_date")}</span>
    }),
    columnHelper.accessor("dueDate", {
      cell: (info) => moment(info.getValue()).format("YYYY-MM-DD"),
      header: () => <span>{t("assignments_modal_calendar_label_due_date")}</span>
    }),
    columnHelper.accessor("name", {
      header: () => t("Name"),
      cell: (info) => info.getValue()
    }),
    columnHelper.accessor("group", {
      header: () => t("Group"),
      cell: (info) => info.getValue()
    }),
    columnHelper.display({
      id: "completion",
      header: () => t("assignment_completion", "Completion"),
      cell: ({ row }) => {
        const { usersCompleted, usersAssigned } = row.original;
        const percent = usersAssigned > 0 ? Math.round((usersCompleted / usersAssigned) * 100) : 0;
        return (
          <ProgressWrapper>
            <ProgressBar $percent={percent} />
            <ProgressLabel>
              {usersCompleted}/{usersAssigned}
            </ProgressLabel>
          </ProgressWrapper>
        );
      }
    }),
    columnHelper.display({
      id: "actions",
      header: `${t("assignments_table_cell_actions")}`,
      cell: ({ cell }) => {
        const [t] = useTranslation();

        return (
          <div className="actionsWrapper">
            <Link to={`${cell.row.original.id}`} type="button" className="btn btn-edit">
              {t("assignments_button_view")}
            </Link>
            <Link
              to={`create/${cell.row.original.id}${
                assignmentsType === assignmentsTypeConfig[0].value ? "/quick-edit" : ""
              }`}
              type="button"
              className="btn btn-error btn-edit"
            >
              {t("assignments_button_edit")}
            </Link>
          </div>
        );
      }
    })
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return isLoading ? (
    <Loader />
  ) : (
    <Layout>
      <div className="assignments-page">
        <div className="main_breadcrumb">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">{t("menu_item_assignments")}</li>
            </ol>
          </nav>
        </div>
        <div
          className="tab-pane fade active show"
          id="contact3-tab-pane"
          role="tabpanel"
          aria-labelledby="contact3-tab"
        >
          <div className="ds-card" ref={assignmentsRef}>
            <div className="assignmensPageHeader d-sm-flex align-items-center  justify-content-between pb-3">
              <h6 className="pro_text">
                {showOverdue
                  ? t("assignments_title_overdue", "Overdue Assignments")
                  : t("assignments_title_assignments")}
              </h6>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <ViewToggleWrapper $isGriffle={isGriffle}>
                  <ViewToggleButton
                    $isGriffle={isGriffle}
                    $isActive={viewMode === "list"}
                    onClick={() => setViewMode("list")}
                  >
                    {t("view_list", "List")}
                  </ViewToggleButton>
                  <ViewToggleButton
                    $isGriffle={isGriffle}
                    $isActive={viewMode === "calendar"}
                    onClick={() => setViewMode("calendar")}
                  >
                    {t("view_calendar", "Calendar")}
                  </ViewToggleButton>
                </ViewToggleWrapper>
                <Link to="create" className="btn custom_primary_btn">
                  {t("assignment_create_btn")}
                </Link>
              </div>
            </div>
            {viewMode === "calendar" ? (
              <AssignmentCalendar
                assignments={data.map((a) => ({
                  id: a.id,
                  name: a.name,
                  startDate: moment(a.startDate).format("YYYY-MM-DD"),
                  dueDate: moment(a.dueDate).format("YYYY-MM-DD"),
                  state: assignmentsType
                }))}
              />
            ) : (
              <div className="assignmentsTableWrapper ds-card sec_body">
                <div className="table-header">
                  <div className="select-text mt-3 mb-0 pb-3 fw-bold">{`${assignmentsFilter?.label} assignments`}</div>
                  <Formik initialValues={{ assignmentsType }} onSubmit={(e) => console.log(e)}>
                    <div className="form-group">
                      <div className="relative">
                        <Field
                          as="select"
                          name="assignmentsType"
                          className="form-control"
                          value={assignmentsType}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                            setAssignmentsType(e.target.value as typeof assignmentsType)
                          }
                        >
                          {assignmentsTypeConfig.map(({ id, value, label }) => {
                            return (
                              <option value={value} key={id}>
                                {label}
                              </option>
                            );
                          })}
                        </Field>
                        <span className="down-img">
                          <img src={arrowDownIcon} alt="icon" />
                        </span>
                      </div>
                    </div>
                  </Formik>
                </div>
                <table className="border-separate border-spacing-0.5 pt-2.5">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id} className={`${row.index % 2 === 0 ? "even" : "odd"}`}>
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Assignments;
